import { addLocale, locale, localeOptions } from "primereact/api";
import type { LocaleOptions } from "primereact/api";

export const CALENDAR_DISPLAY_DATE_FORMAT = "dd/mm/yy";
export const CALENDAR_LOCALE = "es";
const PRIMEREACT_DEFAULT_LOCALE = "en";

// PrimeReact 10.9.7 no tipa collapseLabel, expandLabel, selectLabel, unselectLabel, removeLabel,
// passwordShow ni passwordHide, pero los componentes las leen en runtime. addLocale reemplaza `aria`
// entero, así que esas claves se toman del locale 'en' para que no queden undefined.
const ENGLISH_ARIA_FALLBACK = (localeOptions("en") as LocaleOptions).aria;

const SPANISH_ARIA: NonNullable<LocaleOptions["aria"]> = {
  trueLabel: "Verdadero",
  falseLabel: "Falso",
  nullLabel: "No seleccionado",
  star: "1 estrella",
  stars: "{star} estrellas",
  selectAll: "Todos los elementos seleccionados",
  unselectAll: "Todos los elementos deseleccionados",
  close: "Cerrar",
  previous: "Anterior",
  next: "Siguiente",
  navigation: "Navegación",
  scrollTop: "Ir al inicio",
  moveTop: "Mover al inicio",
  moveUp: "Mover arriba",
  moveDown: "Mover abajo",
  moveBottom: "Mover al final",
  moveToTarget: "Mover al destino",
  moveToSource: "Mover al origen",
  moveAllToTarget: "Mover todo al destino",
  moveAllToSource: "Mover todo al origen",
  pageLabel: "Página {page}",
  otpLabel: "Ingresá el carácter {0} de la contraseña de un solo uso",
  firstPageLabel: "Primera página",
  lastPageLabel: "Última página",
  nextPageLabel: "Página siguiente",
  prevPageLabel: "Página anterior",
  rowsPerPageLabel: "Filas por página",
  jumpToPageDropdownLabel: "Ir a la página (lista desplegable)",
  jumpToPageInputLabel: "Ir a la página (campo numérico)",
  selectRow: "Fila seleccionada",
  unselectRow: "Fila deseleccionada",
  expandRow: "Fila expandida",
  collapseRow: "Fila contraída",
  showFilterMenu: "Mostrar menú de filtros",
  hideFilterMenu: "Ocultar menú de filtros",
  filterOperator: "Operador de filtro",
  filterConstraint: "Condición de filtro",
  editRow: "Editar fila",
  saveEdit: "Guardar edición",
  cancelEdit: "Cancelar edición",
  listView: "Vista de lista",
  gridView: "Vista de cuadrícula",
  slide: "Diapositiva",
  slideNumber: "{slideNumber}",
  zoomImage: "Ampliar imagen",
  zoomIn: "Acercar",
  zoomOut: "Alejar",
  rotateRight: "Girar a la derecha",
  rotateLeft: "Girar a la izquierda",
  listLabel: "Lista de opciones",
};

const SPANISH_CALENDAR_LOCALE: LocaleOptions = {
  firstDayOfWeek: 1,
  dayNames: [
    "domingo",
    "lunes",
    "martes",
    "miércoles",
    "jueves",
    "viernes",
    "sábado",
  ],
  dayNamesShort: ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"],
  dayNamesMin: ["D", "L", "M", "X", "J", "V", "S"],
  monthNames: [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ],
  monthNamesShort: [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sep",
    "oct",
    "nov",
    "dic",
  ],
  today: "Hoy",
  clear: "Limpiar",
  now: "Ahora",
  emptyMessage: "No hay opciones disponibles",
  emptyFilterMessage: "No se encontraron resultados",
  chooseDate: "Elegir fecha",
  chooseMonth: "Elegir mes",
  chooseYear: "Elegir año",
  prevDecade: "Década anterior",
  nextDecade: "Década siguiente",
  prevYear: "Año anterior",
  nextYear: "Año siguiente",
  prevMonth: "Mes anterior",
  nextMonth: "Mes siguiente",
  prevHour: "Hora anterior",
  nextHour: "Hora siguiente",
  prevMinute: "Minuto anterior",
  nextMinute: "Minuto siguiente",
  prevSecond: "Segundo anterior",
  nextSecond: "Segundo siguiente",
  am: "AM",
  pm: "PM",
  weekHeader: "Sem",
  accept: "Sí",
  reject: "No",
  choose: "Elegir",
  upload: "Subir",
  cancel: "Cancelar",
  filter: "Filtro",
  apply: "Aplicar",
  noFilter: "Sin filtro",
  matchAll: "Cumplir todas las reglas",
  matchAny: "Cumplir alguna regla",
  addRule: "Agregar regla",
  removeRule: "Quitar regla",
  custom: "Personalizado",
  startsWith: "Empieza con",
  contains: "Contiene",
  notContains: "No contiene",
  endsWith: "Termina con",
  equals: "Igual a",
  notEquals: "Distinto de",
  lt: "Menor que",
  lte: "Menor o igual que",
  gt: "Mayor que",
  gte: "Mayor o igual que",
  dateIs: "La fecha es",
  dateIsNot: "La fecha no es",
  dateBefore: "La fecha es anterior a",
  dateAfter: "La fecha es posterior a",
  aria: { ...ENGLISH_ARIA_FALLBACK, ...SPANISH_ARIA },
};

let calendarLocaleRegistered = false;

export const registerCalendarLocale = (): void => {
  if (calendarLocaleRegistered) {
    return;
  }

  addLocale(CALENDAR_LOCALE, SPANISH_CALENDAR_LOCALE);
  // PrimeReactProvider vuelve a poner el locale global en 'en' al montar, y DataTable, Dropdown y Dialog
  // leen el global. Con los textos en español también bajo 'en', se ven en español sin importar el orden.
  // InputNumber no cambia: el formato numérico sale de Intl, no de estos textos.
  addLocale(PRIMEREACT_DEFAULT_LOCALE, SPANISH_CALENDAR_LOCALE);
  locale(CALENDAR_LOCALE);
  calendarLocaleRegistered = true;
};
